// Thin shim around `@ping/ui`'s `useIncomingMessages` that injects
// the apps/web Dexie adapters. The actual SDK-event → persistence
// logic lives in `packages/ui/src/sync/useIncomingMessages.ts`;
// keeping a one-line wrapper here lets the AppShell call site stay
// identical (`useIncomingMessages()` with no args) while the shared
// hook receives the host-specific `MessagesPersister` /
// `ConversationsPersister` instances.
//
// Apps/desktop has its own slice and will mount the same shared hook
// with its own adapters — no behavioural drift between platforms.

import {
  type IncomingNotification,
  useIncomingMessages as sharedUseIncomingMessages,
} from '@ping/ui';

import {
  webMeetingEventsPersister,
  webNotesPersister,
  webPollVotesPersister,
  webReactionsPersister,
  webReadPointersPersister,
  webRemindersPersister,
  webTasksPersister,
  webTourSeenPersister,
} from '@core/persistence/db';
import { refreshSelfProfileFromServer } from '@features/profile/refreshSelfProfile';

import { applyConvStatePatch } from './applyConvStatePatch';
import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';
import { webSavedPersister, webStarredPersister } from './personalBookmarks';

export function useIncomingMessages(onNotify?: (evt: IncomingNotification) => void): void {
  sharedUseIncomingMessages({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    ...(onNotify !== undefined ? { onNotify } : {}),
    // A sibling device changed the shared profile → re-pull /v1/me/profile so
    // the new name/avatar shows here in realtime (multi-device profile sync).
    onSelfProfileChanged: () => {
      void refreshSelfProfileFromServer();
    },
    // A sibling device changed a conversation's personal state (mute/archive/
    // pin/favorite/delete/folder) → apply it to our local Dexie row. `eventTs`
    // (the event's HLC wall-clock) lets `applyConvStatePatch` drop a stale,
    // out-of-order patch instead of flip-flopping the conversation's state.
    onConvStatePatch: (conversationId, patch, eventTs) => {
      void applyConvStatePatch(conversationId, patch, eventTs);
    },
    // Persist received `com.ping.reaction` / `reaction_remove` into the
    // shared OR-set so `useReactions` (in WebChatRoom) projects peer
    // reactions live, without a chat-room-scope `onMessage` listener.
    reactions: webReactionsPersister,
    // Persist received `com.ping.poll_vote` so `usePollVotes` tallies
    // peers' votes (not just the local optimistic self-vote).
    pollVotes: webPollVotesPersister,
    // Capture received meeting events (§2.13) — MEETING_CREATE anchors a card
    // row; update/rsvp/cancel are folded into live RSVP state by WebChatRoom.
    meetingEvents: webMeetingEventsPersister,
    // Converge personal bookmarks from the user's OTHER devices in the
    // background: saved (§2.7 0x62/0x63, per-conversation) applies regardless of
    // which room is mounted; starred (§2.7 0x64, DeviceGroup-only) mirrors here.
    saved: webSavedPersister,
    starred: webStarredPersister,
    // Apply DeviceGroup personal state (`com.ping.note_*` / `task_*`,
    // guarded to the `ffdc` DeviceGroup in the shared hook) so notes +
    // tasks sync in from the user's other devices.
    tasks: webTasksPersister,
    notes: webNotesPersister,
    // Apply DeviceGroup activity reminders + read pointers (§2.10, guarded to
    // the `ffdc` DeviceGroup in the shared hook) so a reminder set / item read
    // on the user's other devices mirrors here.
    reminders: webRemindersPersister,
    readPointers: webReadPointersPersister,
    // Apply DeviceGroup product-tour-seen mirrors (§2.10, guarded to the `ffdc`
    // DeviceGroup in the shared hook) so a tour finished on another device
    // suppresses it here via the same `tour.${tourId}.seen` settings_kv flag.
    tourSeen: webTourSeenPersister,
  });
}
